import { Tabs, Tag } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection } from '../../../../../shared/components/section-trail';
import { MAINTENANCE_TAB_LABELS, maintenanceTabPath } from '../../../maintenance.paths';
import { ApprovalReadinessNote } from '../components/ApprovalReadinessNote';
import { VerificationTab } from '../components/VerificationTab';
import { WorkOrderHistory } from '../components/WorkOrderHistory';
import { WorkOrderMovesBar } from '../components/WorkOrderMovesBar';
import { WorkOrderSummary } from '../components/WorkOrderSummary';
import { WorkshopPanel } from '../components/WorkshopPanel';
import { useWorkOrderDetail } from '../hooks/use-work-order-detail';
import { useWorkOrderPermissions } from '../hooks/use-work-order-permissions';
import { WORK_ORDER_STATE_COLORS, WORK_ORDER_STATE_LABELS, workOrderNumber } from '../policies/work-order-labels';
import { WORK_ORDER_TAB_LABELS, WORK_ORDER_TABS, workOrderTabPath, type WorkOrderTab } from '../work-orders.paths';

const TRAIL = withinSection('maintenance', { label: MAINTENANCE_TAB_LABELS['work-orders'], to: maintenanceTabPath('work-orders') });

/** One work order, taken from raised to verified from here. Each tab is its own route (`/maintenance/work-orders/:id/<tab>`). */
export function WorkOrderDetailPage({ tab }: { tab: WorkOrderTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, reload } = useWorkOrderDetail(id);
  const permissions = useWorkOrderPermissions();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading work order" breadcrumbs={TRAIL} tabs={WORK_ORDER_TABS.length} />;
  if (state.status === 'not_found')
    return <EmptyState icon={FileX} title="Work order not found" description="This work order doesn't exist, or the link is out of date." />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this work order" description="Something went wrong loading it. Try reloading the page." />;
  }

  const { order, transitions, readiness } = state;
  const content: Record<WorkOrderTab, ReactNode> = {
    details: (
      <div className="flex flex-col gap-6">
        <ApprovalReadinessNote readiness={readiness} />
        <WorkOrderSummary order={order} />
        <WorkshopPanel order={order} canManage={permissions.canManage} />
      </div>
    ),
    history: <WorkOrderHistory transitions={transitions} currentUserId={permissions.userId} />,
    verification: <VerificationTab order={order} transitions={transitions} permissions={permissions} onRecorded={reload} />,
  };

  return (
    <>
      <PageHeader
        breadcrumbs={TRAIL}
        title={`${workOrderNumber(order.sequenceNumber)} · ${order.title}`}
        actions={<Tag color={WORK_ORDER_STATE_COLORS[order.state]}>{WORK_ORDER_STATE_LABELS[order.state]}</Tag>}
      />
      <WorkOrderMovesBar order={order} readiness={readiness} reload={reload} />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(workOrderTabPath(order.id, key as WorkOrderTab))}
        items={WORK_ORDER_TABS.map((key) => ({ key, label: WORK_ORDER_TAB_LABELS[key], children: key === tab ? content[key] : null }))}
      />
    </>
  );
}
